import { create } from 'zustand';
import type { ColorScheme } from '@/platform';

export type ToastTone = 'info' | 'success' | 'error';

export interface Toast {
  id: string;
  text: string;
  tone: ToastTone;
}

/** Системные состояния из макетов — используются на экране диагностики. */
export type SystemStateId = 'offline' | 'server' | 'access' | 'empty' | 'generation' | 'permission';

interface UiState {
  theme: ColorScheme;
  /** Тема, выбранная пользователем вручную; null — следуем за мессенджером. */
  themeOverride: ColorScheme | null;
  toasts: Toast[];
  /** Незакрытый лист-подтверждение смены профиля. */
  pendingProfileId: string | null;
  /** Показывать ли окно запроса разрешения на уведомления. */
  notificationPromptVisible: boolean;
  isOnline: boolean;

  setTheme(theme: ColorScheme): void;
  setThemeOverride(theme: ColorScheme | null): void;
  toast(text: string, tone?: ToastTone): void;
  dismissToast(id: string): void;
  setPendingProfile(profileId: string | null): void;
  setNotificationPrompt(visible: boolean): void;
  setOnline(isOnline: boolean): void;
}

export const useUiStore = create<UiState>((set, get) => ({
  theme: 'light',
  themeOverride: null,
  toasts: [],
  pendingProfileId: null,
  notificationPromptVisible: false,
  isOnline: navigator.onLine,

  setTheme(theme) {
    set({ theme });
    document.documentElement.dataset.theme = get().themeOverride ?? theme;
  },
  setThemeOverride(themeOverride) {
    set({ themeOverride });
    document.documentElement.dataset.theme = themeOverride ?? get().theme;
  },

  toast(text, tone = 'info') {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    set((state) => ({ toasts: [...state.toasts, { id, text, tone }] }));
    setTimeout(() => get().dismissToast(id), 3200);
  },
  dismissToast(id) {
    set((state) => ({ toasts: state.toasts.filter((toast) => toast.id !== id) }));
  },

  setPendingProfile(pendingProfileId) {
    set({ pendingProfileId });
  },
  setNotificationPrompt(notificationPromptVisible) {
    set({ notificationPromptVisible });
  },
  setOnline(isOnline) {
    set({ isOnline });
  },
}));
